import React, { useState, useEffect } from 'react';
import Card from '../components/Card';
import api from '../services/api';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import Logo from '../components/Logo';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, TextRun, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import htmlToPdfmake from 'html-to-pdfmake';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import UserDataModelsDropdown from '../components/UserDataModelsDropdown';
import '../styles/markdown.css'; // We'll create this file for markdown styling

// Initialize pdfMake with fonts
pdfMake.vfs = pdfFonts.vfs;

const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  return !inline && match ? (
    <SyntaxHighlighter
      style={docco}
      language={match[1]}
      PreTag="div"
      {...props}
    >
      {String(children).replace(/\n$/, '')}
    </SyntaxHighlighter>
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

const SQLCopilot = () => {
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [sqlResult, setSqlResult] = useState(null);
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [dialect, setDialect] = useState('Mysql');
  const [activeAssistant, setActiveAssistant] = useState('sql-generator');
  
  // State for assistants
  const [schemaMessage, setSchemaMessage] = useState('');
  const [schemaResponse, setSchemaResponse] = useState('');
  const [schemaAssistantLoading, setSchemaAssistantLoading] = useState(false);
  
  const [technicalMessage, setTechnicalMessage] = useState('');
  const [technicalResponse, setTechnicalResponse] = useState('');
  const [technicalAssistantLoading, setTechnicalAssistantLoading] = useState(false);
  
  // History states
  const [sqlHistory, setSqlHistory] = useState([]);
  const [schemaHistory, setSchemaHistory] = useState([]);
  const [technicalHistory, setTechnicalHistory] = useState([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // User data model states
  const [selectedUserModel, setSelectedUserModel] = useState(null);

  // Get authenticated user ID using API helper
  const getAuthenticatedUserId = () => {
    return api.getAuthenticatedUserId();
  };

  // Add a new state for user ID
  const [userId, setUserId] = useState("");

  // Add useEffect to get authenticated user ID
  useEffect(() => {
    const authenticatedUserId = getAuthenticatedUserId();
    if (authenticatedUserId) {
      console.log('Using authenticated user ID:', authenticatedUserId);
      setUserId(authenticatedUserId);
    } else {
      console.warn('No authenticated user ID found, using fallback');
      // Fallback to localStorage for backward compatibility
      const storedUserId = localStorage.getItem('copilot_user_id');
      if (storedUserId) {
        setUserId(storedUserId);
      } else {
        // Generate a new user ID only if no authenticated user
        const newUserId = 'user_' + Math.random().toString(36).substring(2, 15) + 
                          Math.random().toString(36).substring(2, 15);
        localStorage.setItem('copilot_user_id', newUserId);
        setUserId(newUserId);
      }
    }
  }, []);

  // Handler functions for user data model selection
  const handleUserModelSelection = (model) => {
    try {
      console.log('Selected model for SQL Copilot:', model);
      setSelectedUserModel(model);
      
      // Extract the schema from the data model and set it
      const schema = model.etl_schema;
      console.log('Extracted schema for SQL Copilot:', schema);
      
      if (!schema) {
        throw new Error('No schema found in the selected data model');
      }
      
      if (!schema.tables || !Array.isArray(schema.tables)) {
        throw new Error('Invalid schema format: missing or invalid tables array');
      }
      
      setSchema(schema);
      console.log('Schema updated for SQL Copilot');
    } catch (err) {
      console.error('Error processing selected model for SQL Copilot:', err);
      setSelectedUserModel(null);
      setSchema(null);
    }
  };

  const clearSelectedUserModel = () => {
    setSelectedUserModel(null);
    setSchema(null);
  };

  // Update user ID when authenticated user changes
  useEffect(() => {
    const authenticatedUserId = getAuthenticatedUserId();
    if (authenticatedUserId && authenticatedUserId !== userId) {
      console.log('Authenticated user changed, updating user ID:', authenticatedUserId);
      setUserId(authenticatedUserId);
    }
  }, [userId]);

  const dialects = [
    { value: 'Snowflake SQL', label: 'Snowflake SQL' },
    { value: 'mysql', label: 'MySQL' },
    //{ value: 'sqlserver', label: 'SQL Server' },
    { value: 'oracle', label: 'Oracle' },
    { value: 'sqlite', label: 'SQLite' },
  ];

  const assistants = [
    { value: 'sql-generator', label: 'SQL Generator' },
    { value: 'schema-assistant', label: 'Schema Assistant' },
    { value: 'technical-assistant', label: 'Technical Assistant' },
  ];

  useEffect(() => {
    const loadSchema = async () => {
      setSchemaLoading(true);
      try {
        const response = await api.fetchSchema(userId);
        if (response && response.data) {
          setSchema(response.data);
        }
      } catch (error) {
        console.error("Error fetching schema:", error);
      } finally {
        setSchemaLoading(false);
      }
    };

    if (userId) {
      loadSchema();
    }
  }, [userId]);
  
  // Load appropriate history when assistant type changes
  useEffect(() => {
    const loadHistory = async () => {
      setHistoryLoading(true);
      setSelectedHistoryItem(null);
      
      try {
        let endpoint = "";
        
        switch (activeAssistant) {
          case 'sql-generator':
            endpoint = "sql-generation-history";
            break;
          case 'schema-assistant':
            endpoint = "schema-chat-history";
            break;
          case 'technical-assistant':
            endpoint = "technical-chat-history";
            break;
          default:
            setHistoryLoading(false);
            return;
        }

        console.log(`Loading history for ${activeAssistant} from endpoint: ${endpoint}`);
        const response = await api.fetchHistory(endpoint, 10, userId);
        
        if (response && response.data) {
          console.log(`Received history data for ${activeAssistant}:`, response.data);
          
          switch (activeAssistant) {
            case 'sql-generator':
              setSqlHistory(response.data);
              break;
            case 'schema-assistant':
              setSchemaHistory(response.data);
              console.log('Schema history state updated:', response.data);
              break;
            case 'technical-assistant':
              setTechnicalHistory(response.data);
              console.log('Technical history state updated:', response.data);
            break;
          default:
            break;
          }
        } else {
          console.warn(`No history data received for ${activeAssistant}`);
        }
      } catch (error) {
        console.error(`Error loading ${activeAssistant} history:`, error);
      } finally {
        setHistoryLoading(false);
      }
    };
    
    loadHistory();
  }, [activeAssistant, userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const response = await api.generateSQL(query, dialect, userId);
      
      if (response && response.data) {
        setSqlResult(response.data);
        // Refresh SQL history after generating new SQL
        const historyResponse = await api.fetchHistory("sql-generation-history", 10, userId);
        if (historyResponse && historyResponse.data) {
          setSqlHistory(historyResponse.data);
        }
      }
    } catch (error) {
      console.error("Error generating SQL:", error);
      setSqlResult({
        error: "Error generating SQL. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSchemaAssistantSubmit = async (e) => {
    e.preventDefault();
    if (!schemaMessage.trim()) return;
    
    setSchemaAssistantLoading(true);
    try {
      console.log('Submitting schema assistant message:', schemaMessage);
      const response = await api.postChatMessage('schema-chat', schemaMessage, userId);
      
      if (response && response.data) {
        console.log('Schema assistant response:', response.data);
        setSchemaResponse(response.data.response || response.data);
        // Refresh schema history after new chat
        try {
          console.log('Refreshing schema chat history...');
        const historyResponse = await api.fetchHistory("schema-chat-history", 10, userId);
        if (historyResponse && historyResponse.data) {
            console.log('Updated schema history:', historyResponse.data);
          setSchemaHistory(historyResponse.data);
          }
        } catch (historyError) {
          console.error("Error refreshing schema history:", historyError);
        }
      }
    } catch (error) {
      console.error("Error with schema assistant:", error);
      setSchemaResponse("Error getting response from schema assistant. Please try again.");
    } finally {
      setSchemaAssistantLoading(false);
    }
  };

  const handleTechnicalAssistantSubmit = async (e) => {
    e.preventDefault();
    if (!technicalMessage.trim()) return;
    
    setTechnicalAssistantLoading(true);
    try {
      console.log('Submitting technical assistant message:', technicalMessage);
      const response = await api.postChatMessage('technical-chat', technicalMessage, userId);
      
      if (response && response.data) {
        console.log('Technical assistant response:', response.data);
        setTechnicalResponse(response.data.response || response.data);
        // Refresh technical history after new chat
        try {
          console.log('Refreshing technical chat history...');
        const historyResponse = await api.fetchHistory("technical-chat-history", 10, userId);
        if (historyResponse && historyResponse.data) {
            console.log('Updated technical history:', historyResponse.data);
          setTechnicalHistory(historyResponse.data);
          }
        } catch (historyError) {
          console.error("Error refreshing technical history:", historyError);
        }
      }
    } catch (error) {
      console.error("Error with technical assistant:", error);
      setTechnicalResponse("Error getting response from technical assistant. Please try again.");
    } finally {
      setTechnicalAssistantLoading(false);
    }
  };

  // Improved function to get the exact HTML as rendered in the browser
  const getRenderedHtml = (content) => {
    // Create a container div
    const container = document.createElement('div');
    container.className = 'markdown-render-container';
    container.style.padding = '20px';
    container.style.maxWidth = '800px';
    container.style.margin = '0 auto';
    container.style.fontFamily = 'Calibri, Arial, sans-serif';
    
    // Create title element
    const title = document.createElement('h1');
    title.textContent = 'SQL Copilot Response';
    title.style.textAlign = 'center';
    title.style.marginBottom = '20px';
    title.style.color = '#333';
    title.style.borderBottom = '1px solid #ddd';
    title.style.paddingBottom = '10px';
    
    container.appendChild(title);
    
    // Create content container
    const contentDiv = document.createElement('div');
    contentDiv.className = 'markdown-content';
    
    // Append to document temporarily to render
    document.body.appendChild(container);
    container.appendChild(contentDiv);
    
    // Use ReactDOM to render the markdown
    const ReactDOM = require('react-dom');
    ReactDOM.render(
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ 
        code: CodeBlock 
      }}>
        {content}
      </ReactMarkdown>,
      contentDiv
    );
    
    // Apply additional styling to tables
    const tables = container.querySelectorAll('table');
    tables.forEach(table => {
      table.style.borderCollapse = 'collapse';
      table.style.width = '100%';
      table.style.marginBottom = '20px';
      table.style.border = '1px solid #ddd';
      
      const headers = table.querySelectorAll('th');
      headers.forEach(header => {
        header.style.backgroundColor = '#f2f2f2';
        header.style.border = '1px solid #ddd';
        header.style.padding = '8px';
        header.style.fontWeight = 'bold';
        header.style.textAlign = 'left';
      });
      
      const cells = table.querySelectorAll('td');
      cells.forEach(cell => {
        cell.style.border = '1px solid #ddd';
        cell.style.padding = '8px';
        cell.style.textAlign = 'left';
      });
      
      const rows = table.querySelectorAll('tr:nth-child(even)');
      rows.forEach(row => {
        row.style.backgroundColor = '#f9f9f9';
      });
    });
    
    // Style code blocks with enhanced SQL syntax highlighting
    const preBlocks = container.querySelectorAll('pre');
    preBlocks.forEach(pre => {
      pre.style.backgroundColor = '#f8f9fa';
      pre.style.padding = '16px';
      pre.style.border = '1px solid #e9ecef';
      pre.style.borderRadius = '6px';
      pre.style.overflow = 'auto';
      pre.style.fontFamily = 'Courier New, monospace';
      pre.style.fontSize = '13px';
      pre.style.lineHeight = '1.5';
      pre.style.margin = '20px 0';
      pre.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
      
      // Enhanced SQL syntax highlighting
      const codeElement = pre.querySelector('code');
      if (codeElement && codeElement.className.includes('language-sql')) {
        // Apply SQL-specific styling
        codeElement.style.color = '#333';
        
        // Style SQL keywords (this will be applied by the syntax highlighter)
        const keywords = codeElement.querySelectorAll('.hljs-keyword');
        keywords.forEach(keyword => {
          keyword.style.color = '#0066cc';
          keyword.style.fontWeight = 'bold';
        });
        
        // Style SQL strings
        const strings = codeElement.querySelectorAll('.hljs-string');
        strings.forEach(string => {
          string.style.color = '#008800';
        });
        
        // Style SQL comments
        const comments = codeElement.querySelectorAll('.hljs-comment');
        comments.forEach(comment => {
          comment.style.color = '#666666';
          comment.style.fontStyle = 'italic';
        });
        
        // Style SQL numbers
        const numbers = codeElement.querySelectorAll('.hljs-number');
        numbers.forEach(number => {
          number.style.color = '#cc6600';
        });
      }
    });
    
    // Style inline code
    const codeInline = container.querySelectorAll('code:not(pre code)');
    codeInline.forEach(code => {
      code.style.fontFamily = 'Courier New, monospace';
      code.style.backgroundColor = '#f8f9fa';
      code.style.padding = '3px 6px';
      code.style.borderRadius = '4px';
      code.style.fontSize = '90%';
      code.style.border = '1px solid #e9ecef';
      code.style.color = '#333';
    });
    
    // Style lists
    const lists = container.querySelectorAll('ul, ol');
    lists.forEach(list => {
      list.style.paddingLeft = '20px';
      list.style.marginBottom = '15px';
      
      const items = list.querySelectorAll('li');
      items.forEach(item => {
        item.style.marginBottom = '5px';
        item.style.lineHeight = '1.5';
      });
    });
    
    // Style headings
    const headings = container.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach(heading => {
      heading.style.marginTop = '20px';
      heading.style.marginBottom = '10px';
      heading.style.lineHeight = '1.2';
      heading.style.color = '#333';
    });
    
    // Style paragraphs
    const paragraphs = container.querySelectorAll('p');
    paragraphs.forEach(p => {
      p.style.marginBottom = '10px';
      p.style.lineHeight = '1.5';
    });
    
    // Get the HTML content
    const html = container.outerHTML;
    
    // Clean up
    document.body.removeChild(container);
    
    return html;
  };

  // Function to format SQL content with proper syntax highlighting
  const formatSQLContent = (sqlContent) => {
    if (!sqlContent) return '';
    
    // Create a formatted markdown content with SQL syntax highlighting
    return `# SQL Query

\`\`\`sql
${sqlContent}
\`\`\`

Generated by SQL Copilot`;
  };

  // Improved downloadAsPDF that captures the browser-rendered content
  const downloadAsPDF = (content) => {
    if (!content) return;
    
    try {
      // Check if content is SQL and format it accordingly
      const formattedContent = content.includes('CREATE TABLE') || content.includes('SELECT') || content.includes('INSERT') || content.includes('UPDATE') || content.includes('DELETE') 
        ? formatSQLContent(content) 
        : content;
      
      // Get the rendered HTML exactly as it appears in the browser
      const renderedHtml = getRenderedHtml(formattedContent);
      
      // Create a new window to print from
      const printWindow = window.open('', '_blank');
      
      // Write the rendered HTML to the new window
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>SQL Copilot Response</title>
          <style>
            @media print {
              body {
                padding: 20px;
                font-size: 12pt;
              }
              
              /* Force page breaks before major headings */
              h1, h2 {
                page-break-before: always;
              }
              
              /* Avoid page breaks after headings */
              h1, h2, h3, h4, h5, h6 {
                page-break-after: avoid;
              }
              
              /* Avoid page breaks inside these elements */
              pre, table, figure {
                page-break-inside: avoid;
              }
              
              /* Hide URL displays in links */
              a:link:after, a:visited:after {
                content: "";
              }
              
              table {
                border-collapse: collapse;
                width: 100%;
              }
              
              th, td {
                border: 1px solid #ddd;
              }
              
              img {
                max-width: 100% !important;
              }
              
              /* SQL syntax highlighting styles */
              pre {
                background-color: #f8f9fa;
                border: 1px solid #e9ecef;
                border-radius: 4px;
                padding: 16px;
                margin: 16px 0;
                overflow-x: auto;
                font-family: 'Courier New', monospace;
                font-size: 11pt;
                line-height: 1.4;
              }
              
              code {
                font-family: 'Courier New', monospace;
                background-color: #f8f9fa;
                padding: 2px 4px;
                border-radius: 3px;
                font-size: 90%;
              }
            }
          </style>
          <script>
            function downloadPDF() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 500);
            }
          </script>
        </head>
        <body>
          ${renderedHtml}
          <script>
            // Automatically trigger printing
            window.onload = downloadPDF;
          </script>
        </body>
        </html>
      `);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('PDF generation failed. Falling back to alternative PDF generation.');
      downloadAsPDFWithPdfMake(content);
    }
  };

  // Alternative PDF generation using pdfmake
  const downloadAsPDFWithPdfMake = (content) => {
    if (!content) return;
    
    try {
      // Check if content is SQL and format it accordingly
      const formattedContent = content.includes('CREATE TABLE') || content.includes('SELECT') || content.includes('INSERT') || content.includes('UPDATE') || content.includes('DELETE') 
        ? formatSQLContent(content) 
        : content;
      
      // Get the rendered HTML
      const renderedHtml = getRenderedHtml(formattedContent);
      
      // Create a temporary div
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = renderedHtml;
      document.body.appendChild(tempDiv);
      
      // Convert HTML to pdfmake content
      const pdfContent = htmlToPdfmake(tempDiv.innerHTML);
      
      // Clean up
      document.body.removeChild(tempDiv);
      
      // Document definition
      const docDefinition = {
        content: pdfContent,
        defaultStyle: {
          font: 'Helvetica',
          fontSize: 12,
          lineHeight: 1.5
        },
        styles: {
          'h1': { fontSize: 24, bold: true, marginBottom: 10, alignment: 'center' },
          'h2': { fontSize: 20, bold: true, marginBottom: 8 },
          'h3': { fontSize: 18, bold: true, marginBottom: 6 },
          'h4': { fontSize: 16, bold: true, marginBottom: 5 },
          'table': { margin: [0, 5, 0, 15] },
          'th': { fillColor: '#f2f2f2', bold: true },
          'pre': { font: 'Courier', fontSize: 10, background: '#f5f5f5', padding: 5, margin: [0, 5, 0, 10] }
        }
      };
      
      // Generate and download PDF
      pdfMake.createPdf(docDefinition).download('sql_copilot_response.pdf');
    } catch (error) {
      console.error('Error in pdfmake generation:', error);
      alert('PDF generation failed. Falling back to basic PDF generation.');
      downloadAsPDFFallback(content);
    }
  };

  const handleDownload = async (sqlContent) => {
    try {
      // Format the SQL content with proper markdown formatting
      const formattedContent = `# SQL Query

\`\`\`sql
${sqlContent}
\`\`\`

Generated by SQL Copilot`;
      
      const response = await api.convertMarkdownToPdf(formattedContent);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'generated_sql.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      // Fallback to local PDF generation
      downloadAsPDF(sqlContent);
    }
  };

  const handleWordDownload = async (sqlContent) => {
    try {
      // Format the SQL content with proper markdown formatting
      const formattedContent = `# SQL Query

\`\`\`sql
${sqlContent}
\`\`\`

Generated by SQL Copilot`;
      
      const response = await api.convertMarkdownToWord(formattedContent);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'generated_sql.docx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Word document:", error);
      // Fallback to local Word generation
      downloadAsWord(sqlContent);
    }
  };

  // Improved Word document generation using browser-rendered HTML
  const downloadAsWord = (content) => {
    if (!content) return;
    
    try {
      // Check if content is SQL and format it accordingly
      const formattedContent = content.includes('CREATE TABLE') || content.includes('SELECT') || content.includes('INSERT') || content.includes('UPDATE') || content.includes('DELETE') 
        ? formatSQLContent(content) 
        : content;
      
      // Get the rendered HTML exactly as it appears in the browser
      const renderedHtml = getRenderedHtml(formattedContent);
      
      // Create a blob with the HTML that's specially formatted for Word
      const blob = new Blob([`
        <!DOCTYPE html>
        <html xmlns:o='urn:schemas-microsoft-com:office:office' 
              xmlns:w='urn:schemas-microsoft-com:office:word'
              xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset="utf-8">
          <title>SQL Copilot Response</title>
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
          </xml>
          <![endif]-->
          <style>
            /* MSO specific styles */
            body {
              font-family: 'Calibri', Arial, sans-serif;
              font-size: 11pt;
              line-height: 1.5;
              width: 8.5in;
              margin: 1in;
            }
            
            table {
              border-collapse: collapse;
              width: 100%;
              mso-table-lspace: 0pt;
              mso-table-rspace: 0pt;
            }
            
            th {
              background-color: #f2f2f2;
              font-weight: bold;
              border: 1px solid #ddd;
              padding: 8pt;
              mso-border-alt: solid #ddd .75pt;
            }
            
            td {
              border: 1px solid #ddd;
              padding: 8pt;
              mso-border-alt: solid #ddd .75pt;
            }
            
            pre {
              font-family: 'Courier New', monospace;
              background-color: #f8f9fa;
              padding: 12pt;
              border: 1px solid #e9ecef;
              white-space: pre-wrap;
              mso-border-alt: solid #e9ecef .75pt;
              border-radius: 4pt;
              margin: 12pt 0;
              font-size: 10pt;
              line-height: 1.4;
            }
            
            code {
              font-family: 'Courier New', monospace;
              background-color: #f8f9fa;
              padding: 2pt 4pt;
              border-radius: 2pt;
              font-size: 9pt;
              border: 1px solid #e9ecef;
            }
            
            code {
              font-family: 'Courier New', monospace;
              background-color: #f5f5f5;
              padding: 2pt 4pt;
            }
            
            /* Preserve table layout */
            table, th, td {
              mso-border-insideh: .75pt solid #ddd;
              mso-border-insidev: .75pt solid #ddd;
            }
            
            /* Keep headings with content */
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
            }
            
            /* Keep code blocks and tables together */
            pre, table {
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          ${renderedHtml}
        </body>
        </html>
      `], { type: 'application/vnd.ms-word;charset=utf-8' });
      
      // Download the file
      saveAs(blob, 'sql_copilot_response.doc');
    } catch (error) {
      console.error('Error creating Word document:', error);
      alert('Word document generation failed. Falling back to alternative method.');
      downloadAsWordAlternative(content);
    }
  };

  // Alternative Word generation method
  const downloadAsWordAlternative = (content) => {
    try {
      // Convert markdown to HTML first
      const htmlContent = getRenderedHtml(content);
      
      // Create a blob with HTML content that Word can open
      const blob = new Blob([`
        <!DOCTYPE html>
        <html xmlns:o='urn:schemas-microsoft-com:office:office' 
              xmlns:w='urn:schemas-microsoft-com:office:word'
              xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset="utf-8">
          <title>SQL Copilot Response</title>
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
          </xml>
          <![endif]-->
          <style>
            /* Document styles */
            body {
              font-family: 'Calibri', sans-serif;
              font-size: 11pt;
              line-height: 1.5;
            }
            h1, h2, h3, h4, h5, h6 {
              font-family: 'Calibri', sans-serif;
              margin-top: 12pt;
              margin-bottom: 6pt;
            }
            h1 { font-size: 18pt; text-align: center; }
            h2 { font-size: 16pt; }
            h3 { font-size: 14pt; }
            h4 { font-size: 12pt; }
            p { margin-bottom: 8pt; }
            table {
              border-collapse: collapse;
              width: 100%;
              margin-bottom: 12pt;
              border: 1px solid #ddd;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 6pt;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            pre {
              font-family: 'Courier New', monospace;
              background-color: #f8f9fa;
              padding: 12pt;
              border: 1px solid #e9ecef;
              border-radius: 4pt;
              margin: 12pt 0;
              font-size: 10pt;
              line-height: 1.4;
              white-space: pre-wrap;
            }
            code {
              font-family: 'Courier New', monospace;
              background-color: #f8f9fa;
              padding: 3pt 6pt;
              border-radius: 3pt;
              font-size: 9pt;
              border: 1px solid #e9ecef;
            }
            ul, ol {
              margin-top: 6pt;
              margin-bottom: 6pt;
            }
            li {
              margin-bottom: 3pt;
            }
          </style>
        </head>
        <body>
          <h1>SQL Copilot Response</h1>
          ${htmlContent}
        </body>
        </html>
      `], { type: 'application/vnd.ms-word;charset=utf-8' });
      
      // Download the file
      saveAs(blob, 'sql_copilot_response.doc');
    } catch (error) {
      console.error('Error in alternative Word generation:', error);
      downloadAsWordFallback(content);
    }
  };

  // Update the clipboard function - add a function to handle copy to clipboard
  const copyToClipboard = (text) => {
    if (!text) return;
    
    try {
      navigator.clipboard.writeText(text)
        .then(() => {
          alert('Content copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
          
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('Content copied to clipboard!');
        });
    } catch (err) {
      console.error('Copy to clipboard failed:', err);
      alert('Failed to copy to clipboard. Please try again.');
    }
  };
  
  // Helper function to extract a short snippet from query text
  const getQuerySnippet = (text, maxLength = 60) => {
    // console.log('Getting query snippet:', text);
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    
  };
  
  // Helper to ensure consistent message field access based on assistant type
  const getMessageField = (item, assistantType) => {
    if (assistantType === 'sql-generator') {
      return item.query;
    } else {
      return item.user_message; // For schema and technical assistants
    }
  };

  // Helper to ensure consistent response field access based on assistant type
  const getResponseField = (item, assistantType) => {
    if (assistantType === 'sql-generator') {
      return item.sql;
    } else {
      return item.ai_response; // For schema and technical assistants
    }
  };
  
  // Function to handle history item selection
  const handleHistoryItemSelect = (item) => {
    setSelectedHistoryItem(item);
    
    // For SQL Generator, we can populate the query field
    if (activeAssistant === 'sql-generator' && item.query) {
      setQuery(item.query);
      setSqlResult(item);
    } 
    // For Schema Assistant, populate the message field with the historical message
    else if (activeAssistant === 'schema-assistant' && item.user_message) {
      setSchemaMessage(item.user_message || '');
      setSchemaResponse(item.ai_response || '');
      
      // Scroll to response area
      setTimeout(() => {
        const responseElement = document.querySelector('.schema-response-area');
        if (responseElement) {
          responseElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    // For Technical Assistant, populate the message field with the historical message
    else if (activeAssistant === 'technical-assistant' && item.user_message) {
      setTechnicalMessage(item.user_message || '');
      setTechnicalResponse(item.ai_response || '');
      
      // Scroll to response area
      setTimeout(() => {
        const responseElement = document.querySelector('.technical-response-area');
        if (responseElement) {
          responseElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };
  
  // Function to render history section
  const renderHistorySection = () => {
    let historyItems = [];
    let historyTitle = "";
    
    switch (activeAssistant) {
      case 'sql-generator':
        historyItems = sqlHistory;
        historyTitle = "SQL Generation History";
        break;
      case 'schema-assistant':
        historyItems = schemaHistory;
        historyTitle = "Schema Chat History";
        break;
      case 'technical-assistant':
        historyItems = technicalHistory;
        historyTitle = "Technical Chat History";
        break;
      default:
        return null;
    }
    
    console.log(`Rendering ${activeAssistant} history:`, historyItems);
    
    return (
      <Card title={historyTitle}>
        {historyLoading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
          </div>
        ) : historyItems && historyItems.length > 0 ? (
          <div className="max-h-96 overflow-y-auto">
            <ul className="divide-y divide-borderLight">
              {historyItems.map((item, index) => {
                // Format date from timestamp
                const date = item.timestamp ? new Date(item.timestamp).toLocaleString() : 'N/A';
                // Get appropriate snippet based on assistant type
                const messageField = getMessageField(item, activeAssistant);
                const snippet = getQuerySnippet(messageField);
                
                return (
                  <li 
                    key={index}
                    className={`p-3 hover:bg-panelHover cursor-pointer ${selectedHistoryItem === item ? 'bg-[rgba(8,145,178,0.08)]' : ''}`}
                    onClick={() => handleHistoryItemSelect(item)}
                  >
                    <div className="flex flex-col">
                      <div className="font-medium text-textPrimary">{snippet}</div>
                      <div className="text-sm text-textSecondary flex items-center gap-2 mt-1">
                        <span>{date}</span>
                        {activeAssistant === 'sql-generator' && item.valid !== undefined && (
                          <>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${item.valid ? 'bg-[rgba(16,185,129,0.15)] text-[#047857]' : 'bg-[rgba(239,68,68,0.15)] text-semanticFail'}`}>
                              {item.valid ? '✓ Valid' : '⚠ Invalid'}
                            </span>
                            {item.dialect && (
                              <span className="px-2 py-0.5 rounded-full bg-[rgba(8,145,178,0.12)] text-accent text-xs">
                                {item.dialect}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <p className="text-textSecondary italic py-4 text-center">No history available.</p>
        )}
      </Card>
    );
  };

  // Update the historical conversation display function
  const formatHistoricalConversation = (message, response) => {
    if (!message && !response) return '';
    
    let formattedHistory = '';
    
    if (message) {
      formattedHistory += `### Your Question:\n\n${message}\n\n`;
    }
    
    if (response) {
      formattedHistory += `### AI Response:\n\n${response}`;
    }
    
    return formattedHistory;
  };

  // Function to download responses as text
  const downloadAsText = (content, filename) => {
    if (!content) return;
    console.log('Downloading text:', content);
    const element = document.createElement("a");
    const file = new Blob([content], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Fallback PDF generation
  const downloadAsPDFFallback = (content) => {
    if (!content) return;
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = 20;
      
      // Set title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('SQL Copilot Response', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      // Check if content is SQL and format it accordingly
      const isSQL = content.includes('CREATE TABLE') || content.includes('SELECT') || content.includes('INSERT') || content.includes('UPDATE') || content.includes('DELETE');
      
      if (isSQL) {
        // Add SQL title
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('SQL Query:', margin, yPosition);
        yPosition += 10;
        
        // Add SQL content with monospace font
        doc.setFontSize(10);
        doc.setFont('courier', 'normal');
        const sqlLines = content.split('\n');
        for (const line of sqlLines) {
          if (yPosition > doc.internal.pageSize.getHeight() - 20) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, margin + 5, yPosition);
          yPosition += 5;
        }
      } else {
        // Add regular content
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        const splitText = doc.splitTextToSize(content, contentWidth);
        doc.text(splitText, margin, yPosition);
      }
      
      // Save the PDF
      doc.save('sql_copilot_response.pdf');
    } catch (error) {
      console.error('Error in fallback PDF generation:', error);
      alert('PDF generation failed. Please try again later.');
    }
  };

  // Fallback Word document generation using docx library
  const downloadAsWordFallback = (content) => {
    if (!content) return;
    
    try {
      // Check if content is SQL and format it accordingly
      const isSQL = content.includes('CREATE TABLE') || content.includes('SELECT') || content.includes('INSERT') || content.includes('UPDATE') || content.includes('DELETE');
      
      const children = [
        new Paragraph({
          text: 'SQL Copilot Response',
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        }),
        new Paragraph({ text: '' })
      ];
      
      if (isSQL) {
        // Add SQL title
        children.push(
          new Paragraph({
            text: 'SQL Query:',
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200 }
          })
        );
        
        // Add SQL content with monospace formatting
        const sqlLines = content.split('\n');
        for (const line of sqlLines) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: line,
                  font: 'Courier New',
                  size: 20, // 10pt
                  spacing: { after: 100 }
                })
              ]
            })
          );
        }
      } else {
        // Add regular content
        children.push(
          new Paragraph({
            text: content,
            spacing: { after: 200 }
          })
        );
      }
      
      // Create a basic document with minimal formatting
      const doc = new Document({
        sections: [{
          properties: {},
          children: children
        }]
      });
      
      // Generate and save document
      Packer.toBlob(doc).then(blob => {
        saveAs(blob, 'sql_copilot_response.docx');
      });
    } catch (error) {
      console.error('Error in fallback Word generation:', error);
      alert('Word document generation failed. Please try again later.');
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-textPrimary">SQL Copilot</h1>
          {/* <Logo className="hidden md:flex" /> */}
        </div>
        
        <div className="mt-6">
          <div className="mb-6 bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="p-4 border-b border-borderLight">
              <label htmlFor="assistant-selector" className="block text-sm font-medium text-textPrimary mb-2">
                Select Assistant
              </label>
              <select
                id="assistant-selector"
                className="w-full md:w-64 px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                value={activeAssistant}
                onChange={(e) => setActiveAssistant(e.target.value)}
              >
                {assistants.map((assistant) => (
                  <option key={assistant.value} value={assistant.value}>
                    {assistant.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* SQL Generator Section */}
        {activeAssistant === 'sql-generator' && (
          <div className="mt-4">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <Card title="Ask for SQL">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label htmlFor="dialect" className="form-label">SQL Dialect</label>
                      <select
                        id="dialect"
                        className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        value={dialect}
                        onChange={(e) => setDialect(e.target.value)}
                      >
                        {dialects.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="query" className="form-label">
                        What SQL do you need?
                      </label>
                      <textarea
                        id="query"
                        className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        rows="6"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Describe in plain English what SQL you need..."
                        required
                      />
                    </div>
                    
                    <div className="mt-6">
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={loading}
                        style={{ background: '#093878' }}
                      >
                        {loading ? 'Generating...' : 'Generate SQL'}
                      </button>
                    </div>
                  </form>
                </Card>
                
                {!loading && sqlResult && (
                  <div className="mt-6">
                    <Card title="Generated SQL">
                      {sqlResult.error ? (
                        <p className="text-semanticFail">{sqlResult.error}</p>
                      ) : (
                        <div>
                          <SyntaxHighlighter language="sql" style={docco}>
                            {sqlResult.sql || '-- No SQL generated'}
                          </SyntaxHighlighter>
                          
                          {sqlResult.explanation && (
                            <div className="mt-4">
                              <h3 className="text-lg font-medium text-textPrimary mb-2">Explanation</h3>
                              <p className="text-textPrimary">{sqlResult.explanation}</p>
                            </div>
                          )}
                          
                          {/* Add validation status indicator */}
                          {sqlResult.valid !== undefined && (
                            <div className={`status-indicator ${sqlResult.valid ? 'status-success' : 'status-error'} mt-4`}>
                              {sqlResult.valid ? 
                                <span>✅ SQL Validated Successfully</span> : 
                                <span>⚠️ Validation Warning: {sqlResult.error || 'Syntax error detected'}</span>
                              }
                            </div>
                          )}
                          
                          <div className="mt-4 flex flex-wrap gap-2">
                            <button
                              className="btn-secondary"
                              onClick={() => downloadAsText(sqlResult.sql, "generated_sql.sql")}
                            >
                              Download SQL
                            </button>
                            <button
                              className="btn-secondary"
                              onClick={() => handleDownload(sqlResult.sql)}
                            >
                              Download as PDF
                            </button>
                            <button
                              className="btn-secondary"
                              onClick={() => handleWordDownload(sqlResult.sql)}
                            >
                              Download as Word
                            </button>
                            <button
                              className="btn-secondary"
                              onClick={() => copyToClipboard(sqlResult.sql)}
                            >
                              Copy to Clipboard
                            </button>
                          </div>
                        </div>
                      )}
                    </Card>
                  </div>
                )}
              </div>
              
              <div>
                {/* History section */}
                {renderHistorySection()}
                
                <div className="mt-6">
                  <Card title="Database Schema">
                    {/* User Data Models Dropdown */}
                    <UserDataModelsDropdown
                      onModelSelect={handleUserModelSelection}
                      onModelClear={clearSelectedUserModel}
                      selectedModel={selectedUserModel}
                      disabled={false}
                      label="Select from Your Data Models:"
                      placeholder="Choose a data model to use as schema..."
                    />
                    
                    {schemaLoading ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                      </div>
                    ) : schema ? (
                      <div className="max-h-96 overflow-y-auto">
                        <SyntaxHighlighter language="json" style={docco}>
                          {JSON.stringify(schema, null, 2)}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <p className="text-textSecondary italic">No schema information available. Select a data model above or upload a schema file.</p>
                    )}
                  </Card>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Schema Functional Assistant Section */}
        {activeAssistant === 'schema-assistant' && (
          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <Card title="Ask About Schema">
                  <form onSubmit={handleSchemaAssistantSubmit}>
                    <div className="mb-4">
                      <label htmlFor="schemaMessage" className="form-label">
                        Ask a question about the database schema
                      </label>
                      <textarea
                        id="schemaMessage"
                        className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        rows="5"
                        value={schemaMessage}
                        onChange={(e) => setSchemaMessage(e.target.value)}
                        placeholder="E.g., What tables are related to customers? How is the inventory tracked?"
                        required
                      />
                    </div>
                    
                    <div className="mt-4">
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={schemaAssistantLoading}
                        style={{ background: '#093878' }}
                      >
                        {schemaAssistantLoading ? 'Processing...' : 'Get Answer'}
                      </button>
                    </div>
                  </form>
                  
                  {schemaAssistantLoading ? (
                    <div className="mt-4 flex justify-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                    </div>
                  ) : schemaResponse ? (
                    <div className="mt-6 bg-panelHover p-4 rounded-md schema-response-area">
                      <h3 className="text-lg font-medium text-textPrimary mb-2">Response</h3>
                      <div className="markdown-content">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]} 
                          components={{ 
                            code: CodeBlock 
                          }}
                        >
                          {schemaResponse}
                        </ReactMarkdown>
                      </div>
                      
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          className="btn-secondary"
                          onClick={() => downloadAsText(schemaResponse, "schema_assistant_response.txt")}
                        >
                          Download as Text
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => handleDownload(schemaResponse)}
                        >
                          Download as PDF
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => handleWordDownload(schemaResponse)}
                        >
                          Download as Word
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => copyToClipboard(schemaResponse)}
                        >
                          Copy to Clipboard
                        </button>
                      </div>
                    </div>
                  ) : null}
                </Card>
              </div>
              
              <div>
                {/* History section */}
                {renderHistorySection()}
                
                {/* Database Schema Section for Schema Assistant */}
                <div className="mt-6">
                  <Card title="Database Schema">
                    {/* User Data Models Dropdown */}
                    <UserDataModelsDropdown
                      onModelSelect={handleUserModelSelection}
                      onModelClear={clearSelectedUserModel}
                      selectedModel={selectedUserModel}
                      disabled={false}
                      label="Select from Your Data Models:"
                      placeholder="Choose a data model to use as schema..."
                    />
                    
                    {schemaLoading ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                      </div>
                    ) : schema ? (
                      <div className="max-h-96 overflow-y-auto">
                        <SyntaxHighlighter language="json" style={docco}>
                          {JSON.stringify(schema, null, 2)}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <p className="text-textSecondary italic">No schema information available. Select a data model above or upload a schema file.</p>
                    )}
                  </Card>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Technical Assistant Section */}
        {activeAssistant === 'technical-assistant' && (
          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <Card title="Technical SQL Help">
                  <form onSubmit={handleTechnicalAssistantSubmit}>
                    <div className="mb-4">
                      <label htmlFor="technicalMessage" className="form-label">
                        Ask a technical question about SQL or databases
                      </label>
                      <textarea
                        id="technicalMessage"
                        className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        rows="5"
                        value={technicalMessage}
                        onChange={(e) => setTechnicalMessage(e.target.value)}
                        placeholder="E.g., How do I optimize a query with multiple joins? What's the difference between LEFT JOIN and INNER JOIN?"
                        required
                      />
                    </div>
                    
                    <div className="mt-4">
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={technicalAssistantLoading}
                        style={{ background: '#093878' }}
                      >
                        {technicalAssistantLoading ? 'Processing...' : 'Get Answer'}
                      </button>
                    </div>
                  </form>
                  
                  {technicalAssistantLoading ? (
                    <div className="mt-4 flex justify-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                    </div>
                  ) : technicalResponse ? (
                    <div className="mt-6 bg-panelHover p-4 rounded-md technical-response-area">
                      <h3 className="text-lg font-medium text-textPrimary mb-2">Response</h3>
                      <div className="markdown-content">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]} 
                          components={{ 
                            code: CodeBlock 
                          }}
                        >
                          {technicalResponse}
                        </ReactMarkdown>
                      </div>
                      
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          className="btn-secondary"
                          onClick={() => downloadAsText(technicalResponse, "technical_assistant_response.txt")}
                        >
                          Download as Text
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => handleDownload(technicalResponse)}
                        >
                          Download as PDF
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => handleWordDownload(technicalResponse)}
                        >
                          Download as Word
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => copyToClipboard(technicalResponse)}
                        >
                          Copy to Clipboard
                        </button>
                      </div>
                    </div>
                  ) : null}
                </Card>
              </div>
              
              <div>
                {/* History section */}
                {renderHistorySection()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SQLCopilot; 